import { SelectableChip as LargeChip } from "@dropbox/dash-component-library";
import { Chip } from "@dropbox/dig-components/chip";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { UpsellPictogram } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashSetupModulePlatform } from "@mirage/analytics/events/enums/dash_setup_module_platform";
import { PAP_Select_DashSetupModulePlatform } from "@mirage/analytics/events/types/select_dash_setup_module_platform";
import { Link } from "@mirage/link/Link";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import useAvailableExtensions from "@mirage/settings/utils/useAvailableExtensions";
import { logoForCurrentBrowser } from "@mirage/shared/util/webExtensionsHelpers";
import i18n, { Trans } from "@mirage/translations";
import { useCallback } from "react";
import { ModuleItemContainer } from "./shared/ModuleItemContainer";
import { useOnHideClick } from "./shared/useOnHideClick";
import { useSetupModuleShown } from "./shared/useSetupModuleShown";
import styles from "./styles.module.css";

const clickDownloadUpsellButton = () => {
  const element = document.querySelector(
    '[data-testid="NavTab-downloadUpsell"]',
  ) as HTMLElement;

  if (element) {
    element.click();
  }
};

export const PlatformModuleItem: React.FC = () => {
  const { onboardingValues, setOnboardingValue } = useOnboardingValues();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const onHideClick = useOnHideClick("add_platforms");
  useSetupModuleShown(
    "add_platforms",
    !!onboardingValues?.dismissedSetupModulePlatforms,
  );

  const handleOnHideClick = () => {
    onHideClick(
      <Trans
        i18nKey={"setup_module_hide_platforms"}
        components={{
          buttonLink: (
            <Link
              variant="monochromatic"
              href="#"
              onClick={clickDownloadUpsellButton}
            />
          ),
        }}
      />,
    );
    setOnboardingValue("dismissedSetupModulePlatforms", true);
  };

  const handleOnClick = useCallback(
    (platform: DashSetupModulePlatform) => {
      reportPapEvent(
        PAP_Select_DashSetupModulePlatform({
          dashActionSurface: "setup_module",
          actionSurfaceComponent: "setup_module",
          featureLine: "onboarding",
          dashSetupModulePlatform: platform,
        }),
      );
    },
    [reportPapEvent],
  );

  if (onboardingValues?.dismissedSetupModulePlatforms) {
    return null;
  }

  return (
    <ModuleItemContainer
      data-testid="setup-module-platforms"
      label={i18n.t("setup_module_platforms_title")}
      leftAccessory={UpsellPictogram}
      onHideClick={handleOnHideClick}
    >
      <Box className={styles.gap8} display="flex">
        <ExtensionChip onClick={() => handleOnClick("browser_extension")} />
      </Box>
    </ModuleItemContainer>
  );
};

type PlatformProps = {
  onClick: () => void;
};

const ExtensionChip = ({ onClick }: PlatformProps) => {
  const { extensionAvailableForBrowser, openExtensionUrl } =
    useAvailableExtensions();

  const handleOnClick = () => {
    openExtensionUrl();
    onClick();
  };

  if (!extensionAvailableForBrowser) {
    return null;
  }

  return (
    <LargeChip onClick={handleOnClick}>
      <LargeChip.LargeIconAccessory>
        <UIIcon src={logoForCurrentBrowser(false)} />
      </LargeChip.LargeIconAccessory>
      <Chip.Content>
        {i18n.t("setup_module_extension_call_to_action")}
      </Chip.Content>
    </LargeChip>
  );
};
